/**
 * @file developmentalEducation.js
 * @author Geoffrey Pursell
 * @desc Developmental Education report

*/

/**
 * @module developmentalEducation
 * @desc Developmental Education report
 */

define('plugins/developmentalEducation',[
  "underscore",
  "models",
  "cardboard/models",
  "cardboard/collections",
  "cardboard/util",
  "cardboard/pluginPieces",
  "text!plugins/templates/developmentalEducation_developmental_table.html!strip",
  "text!plugins/templates/developmentalEducation_institutions_table.html!strip",
  "text!plugins/templates/developmentalEducation_ACT_table.html!strip",
  "text!plugins/templates/developmentalEducation_persistenceDevEd_table.html!strip",
  "text!plugins/templates/developmentalEducation_trend_table.html!strip",
  "text!plugins/templates/developmentalEducation_subjectStuff_table.html",
  "cardboard/pluginPieces",
], function (
  _,
  SLEDSModels,
  CardboardModels,
  CardboardCollections,
  Util,
  PluginPieces,
  developmentalCreditsTemplate,
  institutionsTemplate,
  ACTScoresTemplate,
  persistenceDevEdTemplate,
  trendsTemplate,
  subjectStuffTemplate,
  PluginPieces
) {
  console.log("MODULE: developmentalEducation");

  var exports = {
    // options for the demo cats config. since this is a multi-select
    // report, we have to use a different view object, and tell it not to
    // show grades as categories.
    options: {
      orgDetailPane: false,
      noMapSearch: true,
      forcedSummaryFilters: ["devEdCOHORTID"],
      definitions: true,
    },

    // each of these is the configuration that gets passed to a
    // DataBoxPaneView.
    panes: [
      {
        title: "Developmental Education Enrollment",
        explanationText:
          "This report shows the percent of students enrolled in one or more developmental education credits during the fall term at a Minnesota college, within two years of high school graduation. Developmental education refers to programs offered by postsecondary institutions to prepare students for success in college-level work.",
        openByDefault: true,
        views: [
          {
            type: "template",
            template:
              "<h5>Percent of HS Graduates Enrolled in Developmental Education at a Minnesota College During First Two Fall Terms</h5>",
            dataSourceName: "data",
            dataSetPath: "dataSets.developmentalCredits",
            //errorConditions: [PluginPieces.silentStandardErrorCondition]
          },
          {
            // SETUP
            // one glob = one line series
            // in each series, one row = one data point
            type: "column",
            dataSourceName: "data",
            dataSetPath: "dataSets.developmentalCredits",

            // "pointsWithinRows": given that the data is
            // two-dimensional (meaning an array of arrays, or
            // similar structure, implying that we have more than
            // one series to plot) each series is contained in a
            // "row", like so:
            //	 +---------+
            //	 | X X X X |
            //	 | . . . . |
            //	 | . . . . |
            //	 | . . . . |
            //	 +---------+
            dataShape: "pointsWithinRows",
            dataDimensions: 1,

            // DISPLAY
            // xAxisLabelPath: "label",
            unitString: "%",
            showLegend: false,
            colorByPoint: true,

            // SERIES
            series: [
              {
                seriesDisplayNamePath: "label",
                dataPath: "percentEnrolled.organizations",
                valuePath: "value",
                displayNamePath: "orgName",
                //  colors: ["purple", "orange", "blue2"]
              },
            ],
            errorConditions: [PluginPieces.standardErrorCondition],
          },
          {
            type: "template",
            template: developmentalCreditsTemplate,
            dataSourceName: "data",
            dataSetPath: "dataSets.developmentalCredits",
            //errorConditions: [PluginPieces.silentStandardErrorCondition]
          },
        ],
      },
      {
        title: "Type of Institution Providing Developmental Education",
        explanationText:
          "This report shows the types of institution where students enrolled in developmental education courses.",
        views: [
          {
            type: "template",
            dataSourceName: "data",
            template:
              "<h5>Where HS Graduates Enroll in Developmental Education Within First Two Fall Terms:</h5>",
            dataSetPath: "dataSets.institutions",
            //errorConditions: [PluginPieces.silentStandardErrorCondition]
          },
          {
            // SETUP
            // one glob = one line series
            // in each series, one row = one data point
            type: "stackedColumn",
            dataSourceName: "data",
            dataSetPath: "dataSets.institutions",

            // "pointsWithinRows": given that the data is
            // two-dimensional (meaning an array of arrays, or
            // similar structure, implying that we have more than
            // one series to plot) each series is contained in a
            // "row", like so:
            //	 +---------+
            //	 | X X X X |
            //	 | . . . . |
            //	 | . . . . |
            //	 | . . . . |
            //	 +---------+
            dataShape: "pointsWithinRows",
            dataDimensions: 2,

            // DISPLAY
            xAxisLabelPath: "orgName",
            unitString: "%",
            showLegend: true,

            // SERIES
            series: [
              {
                seriesDisplayNamePath: "longName",
                dataPath: "organizations",
                valuePath: "value",
              },
            ],
            errorConditions: [PluginPieces.standardErrorCondition],
          },
          {
            type: "template",
            template: institutionsTemplate,
            dataSourceName: "data",
            dataSetPath: "dataSets.institutions",
            //errorConditions: [PluginPieces.silentStandardErrorCondition]
          },
        ],
      },
      {
        title: "ACT Scores",
        explanationText:
          "This report shows the average ACT scores of students enrolled in college by developmental education status.",
        views: [
          {
            type: "template",
            dataSourceName: "data",
            dataSetPath: "dataSets.ACTScores",
            template:
              "<h5>ACT Mean Composite Scores by Developmental Education Status:</h5>" +
              "<p>Note: ACT scores vary widely by college of enrollment. Low ACT scores are one reason students can be placed into developmental education.</p>",
            //errorConditions: [PluginPieces.silentStandardErrorCondition]
          },
          {
            // SETUP
            // one glob = one line series
            // in each series, one row = one data point
            type: "column",
            dataSourceName: "data",
            dataSetPath: "dataSets.ACTScores",

            // "pointsWithinRows": given that the data is
            // two-dimensional (meaning an array of arrays, or
            // similar structure, implying that we have more than
            // one series to plot) each series is contained in a
            // "row", like so:
            //	 +---------+
            //	 | X X X X |
            //	 | . . . . |
            //	 | . . . . |
            //	 | . . . . |
            //	 +---------+
            dataShape: "pointsWithinRows",
            dataDimensions: 2,

            // DISPLAY
            xAxisLabelPath: "orgName",
            yAxisMax: 36,
            yAxisTickInterval: 6,
            unitString: "",
            showLegend: true,
            tooltipTemplate:
              "<b>{{formattedValue}}</b><br />{{seriesName}}<br />{{orgName}}",

            // SERIES
            series: [
              {
                seriesDisplayNamePath: "longName",
                dataPath: "organizations",
                valuePath: "value",
                //  colors: ["blue3", "green"]
              },
            ],
            errorConditions: [PluginPieces.standardErrorCondition],
          },
          {
            type: "template",
            template: ACTScoresTemplate,
            dataSourceName: "data",
            dataSetPath: "dataSets.ACTScores",
            //errorConditions: [PluginPieces.silentStandardErrorCondition]
          },
        ],
      },
      {
        title: "Completions By Developmental Education Status",
        explanationText:
          "This report shows the college completion rate of high school graduates who enrolled in developmental education within two years of high school graduation.",
        views: [
          /*  {
                            type: "template",
                            dataSourceName: "data",
                           // template: "<h5>Where HS Graduates Enroll in Developmental Education Within First Two Fall Terms:</h5>",
                            dataSetPath: "dataSets.institutionsCompletions",
                            //errorConditions: [PluginPieces.silentStandardErrorCondition]
                        },*/
          {
            // SETUP
            // one glob = one line series
            // in each series, one row = one data point
            type: "column",
            dataSourceName: "data",
            dataSetPath: "dataSets.institutionsCompletions",

            // "pointsWithinRows": given that the data is
            // two-dimensional (meaning an array of arrays, or
            // similar structure, implying that we have more than
            // one series to plot) each series is contained in a
            // "row", like so:
            //	 +---------+
            //	 | X X X X |
            //	 | . . . . |
            //	 | . . . . |
            //	 | . . . . |
            //	 +---------+
            dataShape: "pointsWithinRows",
            dataDimensions: 2,

            // DISPLAY
            xAxisLabelPath: "orgName",
            unitString: "%",
            showLegend: true,
            tooltipTemplate: "<b>{{seriesName}}</b>:{{formattedValue}}",

            // SERIES
            series: [
              {
                dataPath: "organizations",
                valuePath: "value",
                seriesDisplayNamePath: "longName",
                displayNamePath: "orgID",
              },
            ],
            errorConditions: [PluginPieces.standardErrorCondition],
          },
          {
            type: "template",
            template: institutionsTemplate,
            dataSourceName: "data",
            dataSetPath: "dataSets.institutionsCompletions",
            //errorConditions: [PluginPieces.silentStandardErrorCondition]
          },
        ],
      },
      // TEMPORARY REMOVAL (12/16/2014)
      // Commenting out for now. This will be needed again later.
      // {
      // 	title: "Persistence",
      // 	explanationText: "This report shows the percent of high school graduates enrolled in college and continuing to the 2nd, 3rd or 4th academic year by enrollment in developmental education courses.",
      // 	views: [
      // 		{
      // 			type: "template",
      // 			template: "<h5>Graduates Enrolled in Developmental Education Courses:</h5>",
      // 			dataSourceName: "data",
      // 			dataSetPath: "dataSets.persistence",
      // 			//errorConditions: [PluginPieces.silentStandardErrorCondition]
      // 		},
      // 		{
      // 			// SETUP
      // 			// one glob = one line series
      // 			// in each series, one row = one data point
      // 			type: "column",
      // 			dataSourceName: "data",
      // 			dataSetPath: "dataSets.persistence",
      // 			dataPath: "dataSets.persistence.data.devEd",

      // 			// "pointsWithinRows": given that the data is
      // 			// two-dimensional (meaning an array of arrays, or
      // 			// similar structure, implying that we have more than
      // 			// one series to plot) each series is contained in a
      // 			// "row", like so:
      // 			//	 +---------+
      // 			//	 | X X X X |
      // 			//	 | . . . . |
      // 			//	 | . . . . |
      // 			//	 | . . . . |
      // 			//	 +---------+
      // 			dataShape: "pointsWithinRows",
      // 			dataDimensions: 2,

      // 			// DISPLAY
      // 			xAxisLabelPath: "label",
      // 			unitString: "%",
      // 			showLegend: true,
      // 			tooltipTemplate: "<b>{{formattedValue}}</b><br />{{seriesName}}<br />{{label}}",

      // 			// SERIES
      // 			series: [{
      // 				seriesDisplayNamePath: "orgName",
      // 				dataPath: "year",
      // 				valuePath: "value"
      // 			}],
      // 			//errorConditions: [PluginPieces.standardErrorCondition]
      // 		},
      // 		{
      // 			type: "template",
      // 			template: persistenceDevEdTemplate,
      // 			dataSourceName: "data",
      // 			dataSetPath: "dataSets.persistence",
      // 			dataPath: "dataSets.persistence.data.devEd",
      // 			//errorConditions: [PluginPieces.silentStandardErrorCondition]
      // 		},
      // 		{
      // 			type: "template",
      // 			template: "<h5>Graduates Not Enrolled in Developmental Education Courses:</h5>",
      // 			dataSourceName: "data",
      // 			dataSetPath: "dataSets.persistence",
      // 			//errorConditions: [PluginPieces.silentStandardErrorCondition]
      // 		},
      // 		{
      // 			// SETUP
      // 			// one glob = one line series
      // 			// in each series, one row = one data point
      // 			type: "column",
      // 			dataSourceName: "data",
      // 			dataSetPath: "dataSets.persistence",
      // 			dataPath: "dataSets.persistence.data.nonDevEd",

      // 			// "pointsWithinRows": given that the data is
      // 			// two-dimensional (meaning an array of arrays, or
      // 			// similar structure, implying that we have more than
      // 			// one series to plot) each series is contained in a
      // 			// "row", like so:
      // 			//	 +---------+
      // 			//	 | X X X X |
      // 			//	 | . . . . |
      // 			//	 | . . . . |
      // 			//	 | . . . . |
      // 			//	 +---------+
      // 			dataShape: "pointsWithinRows",
      // 			dataDimensions: 2,

      // 			// DISPLAY
      // 			xAxisLabelPath: "label",
      // 			unitString: "%",
      // 			showLegend: true,
      // 			tooltipTemplate: "<b>{{formattedValue}}</b><br />{{seriesName}}<br />{{label}}",

      // 			// SERIES
      // 			series: [{
      // 				seriesDisplayNamePath: "orgName",
      // 				dataPath: "year",
      // 				valuePath: "value"
      // 			}],
      // 			//errorConditions: [PluginPieces.silentStandardErrorCondition]
      // 		},
      // 		{
      // 			type: "template",
      // 			template: persistenceDevEdTemplate,
      // 			dataSourceName: "data",
      // 			dataSetPath: "dataSets.persistence",
      // 			dataPath: "dataSets.persistence.data.nonDevEd",
      // 			//errorConditions: [PluginPieces.silentStandardErrorCondition]
      // 		},
      // 	]
      // },
      {
        title: "Developmental Education Trends",
        explanationText:
          "This report shows the percent of high school graduates who enrolled in one or more developmental education credits during the first two Fall terms after high school graduation.",
        views: [
          {
            type: "template",
            template:
              "<h5>Percent of HS Graduates Enrolled in Developmental Education During First Two Fall Terms Over Time</h5><p>Note: Data for the most recent year of graduates is considered preliminary.</p>",
            dataSourceName: "data",
            dataSetPath: "dataSets.trends",
            //errorConditions: [PluginPieces.silentStandardErrorCondition]
          },
          {
            // SETUP
            // one glob = one line series
            // in each series, one row = one data point
            type: "line",
            dataSourceName: "data",
            dataSetPath: "dataSets.trends",

            // "pointsWithinRows": given that the data is
            // two-dimensional (meaning an array of arrays, or
            // similar structure, implying that we have more than
            // one series to plot) each series is contained in a
            // "row", like so:
            //	 +---------+
            //	 | X X X X |
            //	 | . . . . |
            //	 | . . . . |
            //	 | . . . . |
            //	 +---------+
            dataShape: "pointsWithinRows",
            dataDimensions: 2,

            // DISPLAY
            xAxisLabelPath: "year",
            unitString: "%",
            showLegend: true,

            // SERIES
            series: [
              {
                seriesDisplayNamePath: "orgName",
                dataPath: "years",
                valuePath: "value",
              },
            ],
            errorConditions: [PluginPieces.standardErrorCondition],
          },
          {
            type: "template",
            template: trendsTemplate,
            dataSourceName: "data",
            dataSetPath: "dataSets.trends",
            //errorConditions: [PluginPieces.silentStandardErrorCondition]
          },
        ],
      },
      {
        title: "Developmental Education by Subject Area",
        explanationText:
          "This report shows the percent of graduates who enrolled in Developmental Education within 2 Years of High School graduation  and the average number of developmental education credits attempted by Developmental Education Subject area. \"N/A\" means data was not available for the selected school, district, or year.",
        openByDefault: false,
        views: [
          {
            // SETUP
            // one glob = one line series
            // in each series, one row = one data point
            type: "column",
            dataSourceName: "data",
            dataPath: "dataSets.subjectStuff.organizations",

            // "pointsWithinRows": given that the data is
            // two-dimensional (meaning an array of arrays, or
            // similar structure, implying that we have more than
            // one series to plot) each series is contained in a
            // "row", like so:
            //	 +---------+
            //	 | X X X X |
            //	 | . . . . |
            //	 | . . . . |
            //	 | . . . . |
            //	 +---------+
            dataShape: "pointsWithinRows",
            dataDimensions: 2,

            // DISPLAY
            // xAxisLabelPath: "label",
            unitString: "%",
            showLegend: true,
            colorByPoint: false,

            // SERIES
            series: [
              {
                seriesDisplayNamePath: "orgName",
                dataPath: "subjects",
                valuePath: "percentEnrolled",
                displayNamePath: "label",
              },
              {
                seriesDisplayNamePath: "orgName",
                dataPath: "subjects",
                valuePath: "percentEnrolled",
                displayNamePath: "label",
              },
              {
                seriesDisplayNamePath: "orgName",
                dataPath: "subjects",
                valuePath: "percentEnrolled",
                displayNamePath: "label",
              },
              {
                seriesDisplayNamePath: "orgName",
                dataPath: "subjects",
                valuePath: "percentEnrolled",
                displayNamePath: "label",
              },
            ],
           // errorConditions: [PluginPieces.standardErrorCondition],
          },
          {
            type: "template",
            template: subjectStuffTemplate,
            dataSourceName: "data",
         //   dataSetPath: "dataSets",
            //errorConditions: [PluginPieces.silentStandardErrorCondition]
          }, 
        ],
      },
      {
        title: "More Information",
        visibleByDefault: true,
        openByDefault: false,
        views: [
          {
            type: "template",
            template:
              "<p><a href='' data-role='button' class='ui-link ui-btn ui-shadow ui-corner-all' role='button'>Download All Data</a></p><p>For more information on college enrollment and developmental education course-taking among Minnesota public high school graduates, please visit the <a href='http://www.ohe.state.mn.us/sPages/GettingPrepared.cfm' target='_blank'>Getting Prepared Reports</a>. These reports include additional information about students taking developmental education including primary language spoken at home and costs of developmental education. </p><p>These reports display graduates according to their last reported year of high school graduation.</p><p><a href='https://education.mn.gov/mdeprod/idcplg?IdcService=GET_FILE&dDocName=mde031877&RevisionSelectionMethod=latestReleased&Rendition=primary'>Download web report data</a> for all high schools and years. Please note this file does not include subgroup statistics. For more information, <a href='https://education.mn.gov/mdeprod/idcplg?IdcService=GET_FILE&dDocName=mde031756&RevisionSelectionMethod=latestReleased&Rendition=primary'>read the help document</a>.</p>",
          },
        ],
      },
    ],

    filterSources: {
      orgs: {
        modelType: SLEDSModels.SLEDSOrgList,
        url: "/ibi_apps/WFServlet?IBIAPP_app=SLEDSwebsvc&IBIF_ex=sledsws_getdata_filter_hsgrad_orgid_deved.fex",
        noViewParams: true,
        enabled: true,
      },
      filters: {
        modelType: CardboardModels.FilterData,
        url: "/ibi_apps/WFServlet?IBIAPP_app=SLEDSwebsvc&IBIF_ex=sledsws_getdata_filter_ecodevregion.fex",
        enabled: true,
      },
      filters2: {
        url: "/ibi_apps/WFServlet?IBIF_ex=sledsws_getdata_filter_developmentaleducationcohortid.fex",
        enabled: true,
        modelType: CardboardModels.FilterData,
      },
      filters3: {
        modelType: CardboardModels.FilterProducer,
        filterSources: ["filterForReport"],
        urls: {
          filterForReport:
            "/ibi_apps/WFServlet?IBIAPP_app=SLEDSwebsvc&IBIF_ex=sledsws_getdata_filter_deveducation_dropdowns.fex",
        },
        enabled: true,
      },
      demographicCategories: {
        modelType: CardboardCollections.DemoCatProducer,
        url: "/ibi_apps/WFServlet?IBIF_ex=sledsws_getdata_filter_demographics.fex",
        enabled: true,
        noViewParams: false,
        isCat: true,
      },
    },
    dataSources: {
      data: {
        modelType: CardboardModels.DocumentModel,
        // url: "/data/fakeDevEdData.json",
        // url: "/data/fakeDevEdDataFromRob.json",
        // url: "/ibi_apps/WFServlet?IBIF_ex=sledsws_getdata_deveducation.fex&HSGRAD_ORGID=000101001&devEdCOHORTID=2008&RACE=_FOC_NULL&GENDER=_FOC_NULL&otherCRITERIA=_FOC_NULL",
        url: "/ibi_apps/WFServlet?IBIF_ex=sledsws_getdata_deveducation.fex",
        yearPath: "dataYear",
        defaultParams: {
          orgId: 999999000,
        },
        paramMappings: {
          orgName: null, // skip this
        },
        postProcessor: function (data) {
          // all we need to do is prettify all the org names!

          var collectionsType1 = [
            data.dataSets.developmentalCredits.data,
            data.dataSets.institutions.data,
            data.dataSets.institutionsCompletions.data,
            data.dataSets.ACTScores.data,
          ];

          var collectionsType2 = [
            data.dataSets.persistence.data.devEd,
            data.dataSets.persistence.data.nonDevEd,
            data.dataSets.trends.data,
            data.dataSets.subjectStuff.organizations,
          ];

          _(collectionsType1).each(function (collection) {
            _(collection).each(function (series) {
              _(series.organizations).each(function (blob) {
                blob.orgName = Util.Data.prettifyName(blob.orgName);
              });
            });
          });

          _(collectionsType2).each(function (collection) {
            _(collection).each(function (blob) {
              blob.orgName = Util.Data.prettifyName(blob.orgName);
            });
          });
        },
        enabled: true,
        errorConditions: [
          {
            valuePath: "meta.dataNotAvailableMessage",
            isNot: null,
            error: {
              type: "NoData",
              messagePath: "meta.dataNotAvailableMessage", //"Data is not available for current selection(s)."
            },
          },
        ],
      },
    },
  };

  return exports;
});

